import React, { useMemo, useState } from "react";
import { Grid, Typography, Theme, Icon } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { FormattedDate, defineMessages } from "react-intl";
import {
	MyPageButton,
	MyPagePaper,
	MyPagePanel,
	MyPageDataTable,
} from "../../common";
import { FormattedMessage, useIntl } from "react-intl";
import {
	BackofficeApplicationResource,
	InvoicesResource,
	AccountTransactionResource,
	PaymentNoticesResource,
} from "../../../resources";
import { useCommonWidths } from "../../../hooks";
import clsx from "clsx";
import { InvoiceTable } from "../../common/InvoiceTable";

const useStyles = makeStyles((theme: Theme) => ({
	panelWrapper: {
		margin: 0,
	},
	paper: {
		padding: theme.spacing(2),
		[theme.breakpoints.up("md")]: {
			minHeight: 444,
			padding: theme.spacing(3),
		},
	},
	overviewBoxTitle: {
		fontWeight: "bold",
	},
	overviewBoxContentWrapper: {
		marginTop: theme.spacing(3),
		width: "inherit",
	},
	minWidth: { [theme.breakpoints.up("md")]: { minWidth: 568 } },
}));

const usePaymentInsuranceStyles = makeStyles((theme: Theme) => ({
	headerAlignment: {
		alignItems: "center",
	},
	headerTextMargin: {
		marginLeft: theme.spacing(2),
	},
	salesText: {
		whiteSpace: "pre-line",
		fontSize: "0.9rem",
	},
	orderText: {
		fontWeight: 600,
	},
	flex: {
		flex: 1,
	},
}));

interface OverviewTabPageProps {
	backofficeApplication: BackofficeApplicationResource;
	invoices: InvoicesResource[];
	paymentNotices: PaymentNoticesResource[];
	// accountTransactions: AccountTransactionResource[];
	onShowMore: (n: number) => void;
	isFulfilled: boolean;
	actionControl?: React.ReactNode;
}

interface DataOverviewBoxProps {
	title: string | React.ReactNode;
	action?: "viewMore" | React.ReactNode;
	setTabValue?: () => void;
	children?: React.ReactNode;
}

const OverviewTabPage = (props: OverviewTabPageProps) => {
	const {
		backofficeApplication,
		invoices,
		paymentNotices,
		// accountTransactions,
		onShowMore,
		isFulfilled,
		actionControl,
	} = props;
	const intl = useIntl();

	const isMobile = useCommonWidths().isSmDown;
	const isXlScreen = useCommonWidths().isXlUp;
	const classes = useStyles();

	const paymentInsuranceStyles = usePaymentInsuranceStyles();

	const collectedInvoicesAndNotices: any[] = [];
	collectedInvoicesAndNotices.push(...invoices);
	collectedInvoicesAndNotices.push(...paymentNotices);
	collectedInvoicesAndNotices
		.sort((a, b) => {
			//Sorter med eldste først:
			return (
				new Date(a.dueDate || a.dateSent).getTime() -
				new Date(b.dueDate || b.dateSent).getTime()
			);
		})
		.reverse();

	return (
		<MyPagePanel fullWidth>
			<Grid container item spacing={isMobile ? 4 : 6}>
				<Grid
					item
					xs={isMobile ? 12 : isXlScreen ? 4 : 6}
					className={classes.minWidth}
				>
					<DataOverviewBox
						title={
							<FormattedMessage
								id="minside.backofficeApplications.backofficeApplicaionData.your_backofficeApplication"
								defaultMessage="Avtalen din"
								description="Your backofficeApplication label"
							/>
						}
						action={!isFulfilled && actionControl}
					>
						<MyPageDataTable
							data={backofficeApplication}
							columns={[
								{
									label: (
										<FormattedMessage
											id="minside.backofficeApplications.backofficeApplicationData.backofficeApplicationNumber"
											defaultMessage="Kontraktsnummer"
											description="BackofficeApplication's Contractnumber"
										/>
									),
									value: backofficeApplication.id,
								},
								{
									label: (
										<FormattedMessage
											id="minside.backofficeApplications.backofficeApplicationData.backofficeApplicationRegNr"
											defaultMessage="Reg. nummer"
											description="BackofficeApplication's Registrationnumber"
										/>
									),
									value: backofficeApplication.regNumber || " - ",
								},
								{
									label: (
										<FormattedMessage
											id="minside.backofficeApplications.backofficeApplicationData.loanAmount.label"
											defaultMessage="Opprinnelig lånebeløp"
											description="Loan amount backofficeApplication label"
										/>
									),
									value: (
										<FormattedMessage
											id="minside.backofficeApplications.backofficeApplicationData.loanAmount.value"
											defaultMessage="{loanAmount}"
											description="Total loan backofficeApplication value"
											values={{
												loanAmount: Intl.NumberFormat(intl.locale, {
													style: "currency",
													currency: "NOK",
													maximumFractionDigits: 0,
													minimumFractionDigits: 0,
												}).format(backofficeApplication.loanAmount || 0),
											}}
										/>
									),
									hideIfCondition: isFulfilled,
								},
								{
									label: isFulfilled ? (
										<FormattedMessage
											id="minside.backofficeApplications.backofficeApplicationData.paid"
											defaultMessage="Totalt nedbetalt lån"
											description="Total paid label"
										/>
									) : (
										<FormattedMessage
											id="minside.backofficeApplications.backofficeApplicationData.remaining"
											defaultMessage="Gjenstående beløp"
											description="Remaining backofficeApplication label"
										/>
									),
									value: (
										<FormattedMessage
											id="minside.backofficeApplications.backofficeApplicationData.remaining.value"
											defaultMessage="{amount}"
											description="Remaining backofficeApplication value"
											values={{
												amount: Intl.NumberFormat(intl.locale, {
													style: "currency",
													currency: "NOK",
													maximumFractionDigits: 0,
													minimumFractionDigits: 0,
												}).format(
													isFulfilled
														? backofficeApplication.totalCost
														: backofficeApplication.remainingCost || 0
												),
											}}
										/>
									),
								},
								{
									label: isFulfilled ? (
										<FormattedMessage
											id="minside.backofficeApplications.backofficeApplicationData.payment_time"
											defaultMessage="Total nedbetalingstid"
											description="Total payment time label"
										/>
									) : (
										<FormattedMessage
											id="minside.backofficeApplications.backofficeApplicationData.remaining_payment_time"
											defaultMessage="Gjenstående nedbetalingstid"
											description="Remaining payment time label"
										/>
									),
									value: (
										<FormattedMessage
											id="minside.backofficeApplications.backofficeApplicationData.payment_time.value"
											defaultMessage="{terms} terminer"
											description="Remaining/total payment time value"
											values={{
												terms: isFulfilled
													? backofficeApplication.terms.reduce(
															(tot, curr) => tot + curr,
															0
													  )
													: backofficeApplication.remainingTerms,
											}}
										/>
									),
								},
								{
									label:
										backofficeApplication.remainingCost <= 0 ? (
											<FormattedMessage
												id="minside.backofficeApplications.backofficeApplicationData.fulfillment-paid"
												defaultMessage="Oppgjørsdato"
												description="Fulfillment date label fulfilled contract"
											/>
										) : (
											<FormattedMessage
												id="minside.backofficeApplications.backofficeApplicationData.fulfillment"
												defaultMessage="Opprinnelig oppgjørsdato"
												description="Fulfillment date label"
											/>
										),
									value: backofficeApplication.dueDate ? (
										<FormattedDate
											year="numeric"
											month="2-digit"
											day="2-digit"
											value={new Date(backofficeApplication.dueDate)}
										/>
									) : (
										<FormattedMessage
											id="minside.backofficeApplications.backofficeApplicationData.duedate.not_available"
											defaultMessage="Ikke tilgjengelig"
											description="Due date not available"
										/>
									),
								},
								{
									label: (
										<FormattedMessage
											id="minside.backofficeApplications.backofficeApplicationData.interest"
											defaultMessage="Rente"
											description="Interest label"
										/>
									),
									value:
										backofficeApplication.updatedInterestRateAfterChange ??
										backofficeApplication.interestRate ? (
											<FormattedMessage
												id="minside.backofficeApplications.backofficeApplicationData.updatedInterestRate.not_available"
												defaultMessage="{updatedInterestRateAfterChange} %"
												description="Updated interestrate not available"
												values={{
													updatedInterestRateAfterChange: Intl.NumberFormat(
														intl.locale,
														{
															maximumFractionDigits: 2,
															minimumFractionDigits: 2,
														}
													).format(
														(backofficeApplication.updatedInterestRateAfterChange ??
															backofficeApplication.interestRate) * 100 || 0
													),
												}}
											/>
										) : (
											<FormattedMessage
												id="minside.backofficeApplications.backofficeApplicationData.intrest_rate.not_available"
												defaultMessage="Ikke tilgjengelig"
												description="Intrest rate not available"
											/>
										),
								},
								{
									label: (
										<FormattedMessage
											id="minside.backofficeApplications.backofficeApplicationData.hasPaymentInsurance_label"
											defaultMessage="Betalingsforsikring"
											description="hasPaymentInsurance-label"
										/>
									),
									value: (
										<FormattedMessage
											id="minside.backofficeApplications.backofficeApplicationData.hasPaymentInsurance_value"
											defaultMessage="Ja"
											description="hasPaymentInsurance_value"
										/>
									),
									hideIfCondition: !backofficeApplication.hasPaymentInsurance,
								},
							]}
						/>
					</DataOverviewBox>
				</Grid>

				<Grid
					className={classes.minWidth}
					item
					xs={isMobile ? 12 : isXlScreen ? 4 : 6}
				>
					<DataOverviewBox
						title={
							<FormattedMessage
								id="minside.backofficeApplications.backofficeApplicationData.invoices"
								defaultMessage="Fakturaer"
								description="Invoices label"
							/>
						}
						action="viewMore"
						{...props}
						setTabValue={() => onShowMore(1)}
					>
						<InvoiceTable
							backofficeApplicationId={backofficeApplication.id}
							collectedInvoicesAndNotices={collectedInvoicesAndNotices}
							limit={7}
						/>
					</DataOverviewBox>
				</Grid>

				{!backofficeApplication.hasPaymentInsurance &&
					backofficeApplication.remainingCost > 0 && (
						<Grid
							className={classes.minWidth}
							item
							xs={isMobile ? 12 : isXlScreen ? 4 : 6}
						>
							<DataOverviewBox
								title={
									<FormattedMessage
										id="minside.backofficeApplications.paymentInsuranceBox.name"
										defaultMessage="Betalingsforsikring"
										description="PaymentInsuranceBox name"
									/>
								}
								{...props}
							>
								<Grid container spacing={2}>
									<Grid
										item
										container
										className={paymentInsuranceStyles.headerAlignment}
									>
										<Grid item>
											<Icon
												color="secondary"
												fontSize="large"
												className="far fa-fw fa-smile"
											/>
										</Grid>
										<Grid
											item
											className={clsx(
												paymentInsuranceStyles.flex,
												paymentInsuranceStyles.headerTextMargin
											)}
										>
											<h2>
												<FormattedMessage
													{...paymentInsuranceMessages.header}
												/>
											</h2>
										</Grid>
									</Grid>

									<Grid item xs={12}>
										<Typography className={paymentInsuranceStyles.salesText}>
											<FormattedMessage
												{...paymentInsuranceMessages.salesText}
											/>
										</Typography>
									</Grid>
									<Grid item>
										<Typography className={paymentInsuranceStyles.orderText}>
											<FormattedMessage
												{...paymentInsuranceMessages.orderText}
											/>
										</Typography>
									</Grid>
									<Grid item>
										<Typography>
											<a
												href="https://signering.financiering.no/media/1002/produktark_betalingsforsikring_as_financiering.pdf"
												target="_blank"
												rel="noopener noreferrer"
											>
												<FormattedMessage
													{...paymentInsuranceMessages.linkText}
												/>
											</a>
										</Typography>
									</Grid>
								</Grid>
							</DataOverviewBox>
						</Grid>
					)}

				{/* Last Transactions table is not part of MinSide scope. This will (hopefully) be ordered as a new feature */}
				{/* <Grid item xs={isMobile ? 12 : isXlScreen ? 4 : 6}>
					<DataOverviewBox
					title={
						<FormattedMessage
								id="minside.backofficeApplications.backofficeApplicationData.last_transactions"
								defaultMessage="Siste bevegelser"
								description="Last transactions label"
							/>
						}
						action="viewMore"
						setTabValue={() => onShowMore(2)}
						{...props}
					>
						<MyPageDataTable
							data={accountTransactions}
							columns={[
								{
									title: (
										<FormattedMessage
											id="minside.loans.tables.date"
											defaultMessage="Dato"
											description="Date label"
										/>
									),
									field: "transactionDate",
									type: "date",
								},
								{
									title: isMobile ? (
										<FormattedMessage
											id="minside.loans.tables.mobile.description"
											defaultMessage="Beskr."
											description="Description label for mobile"
										/>
									) : (
										<FormattedMessage
											id="minside.loans.tables.description"
											defaultMessage="Beskrivelse"
											description="Description label for desktop"
										/>
									),
									field: "description",
								},
								{
									title: (
										<FormattedMessage
											id="minside.loans.tables.sum"
											defaultMessage="Sum"
											description="Sum label"
										/>
									),
									field: "transactionAmount",
								},
							]}
							limit={5}
						/>
					</DataOverviewBox>
				</Grid> */}
			</Grid>
		</MyPagePanel>
	);
};

const DataOverviewBox = (props: DataOverviewBoxProps) => {
	const { title, setTabValue, action } = props;
	const classes = useStyles();

	const isMobile = useCommonWidths().isSmDown;

	return (
		<MyPagePaper style={{ minHeight: 560 }}>
			<Grid container direction="column">
				<Grid container direction="row" alignItems="center">
					<Grid item style={{ flexGrow: 1 }}>
						<Typography
							className={classes.overviewBoxTitle}
							variant={isMobile ? "h6" : "h5"}
						>
							{title}
						</Typography>
					</Grid>
					{action === "viewMore" && (
						<Grid item>
							<MyPageButton
								nextIcon
								onClick={() => setTabValue && setTabValue()}
							>
								<FormattedMessage
									id="minside.backofficeApplications.backofficeApplicationData.all"
									defaultMessage="Se alle"
									description="View all label"
								/>
							</MyPageButton>
						</Grid>
					)}
					{action && typeof action !== "string" && <Grid item>{action}</Grid>}
				</Grid>
				<Grid item className={classes.overviewBoxContentWrapper}>
					{props.children}
				</Grid>
			</Grid>
		</MyPagePaper>
	);
};

const paymentInsuranceMessages = defineMessages({
	header: {
		id: "minside.backofficeApplications.paymentInsuranceBox.header",
		defaultMessage: "En tryggere betalingsopplevelse",
		description: "Header text for paymentinsurance",
	},
	salesText: {
		id: "minside.backofficeApplications.paymentInsuranceBox.salesText",
		defaultMessage: `Med betalingsforsikring på ditt lån i AS Financiering er du og din familie godt sikret om noe uforutsett skulle inntreffe.

		Vår betalingsforsikring dekker hele terminbeløpet ved arbeidsledighet og sykmelding i opptil 12 betalingsmåneder.

		Vi dekker også restgjeld ved kritisk sykdom eller ved tap av liv - gir økonomisk trygghet og én bekymring mindre i en ellers vanskelig situasjon.`,
		description: "Description salestext for paymentinsurance",
	},
	orderText: {
		id: "minside.backofficeApplications.paymentInsuranceBox.orderText",
		defaultMessage:
			"Send oss en mail på {mail} om du ønsker å bestille betalingsforsikring",
		description: "Order text for paymentinsurance",
		values: {
			mail: <a href="mailto:post@financiering.no">post@financiering.no</a>,
		},
	},
	linkText: {
		id: "minside.backofficeApplications.paymentInsuranceBox.pdfLink",
		defaultMessage: "Les mer om vår forsikring",
		description: "Text to read more about paymentinsurance",
	},
});

export default OverviewTabPage;
